const MIME_TO_EXT = {
	"image/png": "png",
	"image/jpeg": "jpg",
	"image/jpg": "jpg",
	"image/gif": "gif",
	"image/webp": "webp",
};

function getExtensionFromDataUrl(dataUrl)
{
	if (typeof dataUrl !== "string" || !dataUrl.startsWith("data:")) return null;
	const match = dataUrl.match(/^data:([^;]+);/);
	if (!match) return null;
	const mime = match[1].toLowerCase();
	return MIME_TO_EXT[mime] || mime.replace("image/", "") || null;
}

function getExtensionFromUrl(url)
{
	try {
		const pathname = new URL(url, window.location.origin).pathname;
		const match = pathname.match(/\.([a-z0-9]+)$/i);
		return match ? match[1].toLowerCase() : null;
	} catch {
		return null;
	}
}

export default class BLightbox
{
	constructor(dialog)
	{
		this.dialog = dialog;
		this.images = JSON.parse(dialog.dataset.lightboxImages || "[]");
		this.currentIndex = 0;
		this.touchStartX = 0;
		this.touchEndX = 0;

		this.mainImage = dialog.querySelector(".b-lightbox__image");
		this.counter = dialog.querySelector(".b-lightbox__counter");
		this.prevButton = dialog.querySelector(".b-lightbox__prev");
		this.nextButton = dialog.querySelector(".b-lightbox__next");
		this.thumbnailsContainer = dialog.querySelector(".b-lightbox__thumbnails");

		this.addEventListeners();
	}

	static initialize()
	{
		document.addEventListener("click", event => {
			const trigger = event.target.closest("[data-lightbox-target]");
			if (!trigger) return;

			event.preventDefault();
			const dialogId = trigger.dataset.lightboxTarget;
			const dialog = document.getElementById(dialogId);
			if (!dialog) return;

			const startIndex = parseInt(trigger.dataset.lightboxIndex || "0", 10);
			const instance = new BLightbox(dialog);
			instance.open(startIndex);
		});
	}

	addEventListeners()
	{
		this.dialog.addEventListener("keydown", event => {
			switch (event.key) {
				case "ArrowLeft":
					event.preventDefault();
					this.prev();
					break;
				case "ArrowRight":
					event.preventDefault();
					this.next();
					break;
				case "Escape":
					this.dialog.close();
					break;
			}
		});

		this.prevButton?.addEventListener("click", () => this.prev());
		this.nextButton?.addEventListener("click", () => this.next());

		this.dialog.addEventListener("click", event => {
			if (event.target === this.dialog) {
				this.dialog.close();
			}
		});

		this.dialog.querySelector(".b-lightbox__close")?.addEventListener("click", () => {
			this.dialog.close();
		});

		this.dialog.querySelector(".b-lightbox__download")?.addEventListener("click", () => {
			this.downloadCurrentImage();
		});

		const viewport = this.dialog.querySelector(".b-lightbox__viewport");
		if (viewport) {
			viewport.addEventListener("touchstart", event => {
				this.touchStartX = event.changedTouches[0].screenX;
			}, { passive: true });

			viewport.addEventListener("touchend", event => {
				this.touchEndX = event.changedTouches[0].screenX;
				this.handleSwipe();
			}, { passive: true });
		}

		this.thumbnailsContainer?.addEventListener("click", event => {
			const thumb = event.target.closest(".b-lightbox__thumbnail");
			if (!thumb) return;
			const index = parseInt(thumb.dataset.index, 10);
			this.goTo(index);
		});
	}

	handleSwipe()
	{
		const diff = this.touchStartX - this.touchEndX;
		const threshold = 50;

		if (Math.abs(diff) < threshold) return;

		if (diff > 0) {
			this.next();
		} else {
			this.prev();
		}
	}

	open(startIndex = 0)
	{
		this.goTo(startIndex);
		const scrollX = window.scrollX;
		const scrollY = window.scrollY;
		this.dialog.showModal();
		window.scrollTo(scrollX, scrollY);
		requestAnimationFrame(() => window.scrollTo(scrollX, scrollY));
	}

	goTo(index)
	{
		if (index < 0 || index >= this.images.length) return;

		this.currentIndex = index;
		const image = this.images[index];

		this.mainImage.src = image.src;
		if (image.srcset) {
			this.mainImage.srcset = image.srcset;
		} else {
			this.mainImage.removeAttribute("srcset");
		}
		this.mainImage.alt = image.alt || "";

		if (this.counter) {
			this.counter.textContent = `${index + 1} / ${this.images.length}`;
		}

		this.prevButton?.classList.toggle("b-lightbox__nav--hidden", index === 0);
		this.nextButton?.classList.toggle("b-lightbox__nav--hidden", index === this.images.length - 1);

		this.thumbnailsContainer?.querySelectorAll(".b-lightbox__thumbnail").forEach((thumb, i) => {
			thumb.classList.toggle("b-lightbox__thumbnail--active", i === index);
		});

		this.scrollThumbnailIntoView(index);
	}

	scrollThumbnailIntoView(index)
	{
		const thumb = this.thumbnailsContainer?.querySelector(`[data-index="${index}"]`);
		if (thumb) {
			thumb.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
		}
	}

	prev()
	{
		if (this.currentIndex > 0) {
			this.goTo(this.currentIndex - 1);
		}
	}

	next()
	{
		if (this.currentIndex < this.images.length - 1) {
			this.goTo(this.currentIndex + 1);
		}
	}

	downloadCurrentImage()
	{
		if (!this.mainImage?.src) return;
		const src = this.mainImage.src;
		let ext = getExtensionFromDataUrl(src);
		if (!ext) {
			ext = getExtensionFromUrl(src) || "jpg";
		}
		const filename = `image-${this.currentIndex + 1}.${ext}`;
		const a = document.createElement("a");
		a.href = src;
		a.download = filename;
		a.style.display = "none";
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
	}
}
